import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, List, Placeholder, Section, SegmentedControl, Spinner } from '@telegram-apps/telegram-ui';
import { GenerationCard } from '../components/GenerationCard';
import { useGenerations } from '../hooks/useGenerations';
import { ErrorState, LoadingState } from '../components/StateViews';

type StatusGroup = 'all' | 'active' | 'completed' | 'failed';

const FILTERS: { key: StatusGroup; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'active', label: 'В работе' },
  { key: 'completed', label: 'Готовые' },
  { key: 'failed', label: 'Ошибки' },
];

export function GenerationsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [statusGroup, setStatusGroup] = useState<StatusGroup>('all');
  const q = useGenerations({ limit: 20, productId: params.get('productId') ?? undefined });

  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const visible = useMemo(() => {
    if (statusGroup === 'all') return items;
    if (statusGroup === 'active') {
      return items.filter((g) =>
        g.status === 'pending' || g.status === 'preprocessing' || g.status === 'processing',
      );
    }
    if (statusGroup === 'completed') return items.filter((g) => g.status === 'completed');
    return items.filter((g) => g.status === 'failed');
  }, [items, statusGroup]);

  if (q.isPending && !q.data) return <LoadingState />;
  if (q.error) {
    const err = q.error instanceof Error ? q.error : new Error(String(q.error));
    return <ErrorState error={err} onRetry={() => void q.refetch()} />;
  }

  return (
    <List>
      <div style={{ display: 'flex', gap: 8, padding: '8px 16px' }}>
        <Button size="s" mode="filled" onClick={() => navigate('/studio/new')}>
          ✨ Новая фотосессия
        </Button>
        <Button size="s" mode="outline" onClick={() => navigate('/import')}>
          📥 Импорт
        </Button>
      </div>
      <Section header="Генерации">
        <SegmentedControl>
          {FILTERS.map((f) => (
            <SegmentedControl.Item key={f.key} selected={statusGroup === f.key} onClick={() => setStatusGroup(f.key)}>
              {f.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      </Section>

      <Section header="Список">
        {q.isFetching && items.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
            <Spinner size="s" />
          </div>
        )}

        {visible.length === 0 ? (
          <Placeholder
            header="Ещё нет генераций"
            description="Откройте товар и нажмите Сгенерировать фото"
          />
        ) : (
          visible.map((g) => <GenerationCard key={g.id} generation={g} onClick={() => navigate(`/generations/${g.id}`)} />)
        )}

        {q.hasNextPage && (
          <div style={{ marginTop: 12 }}>
            <Button
              mode="filled"
              size="m"
              stretched
              loading={q.isFetchingNextPage}
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
            >
              Загрузить ещё
            </Button>
          </div>
        )}
      </Section>
    </List>
  );
}
